import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  message,
  Popconfirm,
  Card,
  Spin,
  Space,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  ReloadOutlined,
  CheckOutlined,
  StopOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";

const API_URL = process.env.REACT_APP_API_BASE_URL || "";

interface Review {
  id: number;
  name: string;
  surname: string;
  text: string;
  created_at: string;
  status: "pending" | "approved" | "rejected";
}

const statusColors: Record<string, string> = {
  pending: "orange",
  approved: "green",
  rejected: "red",
};

const statusLabels: Record<string, string> = {
  pending: "На модерации",
  approved: "Одобрен",
  rejected: "Отклонён",
};

const AdminReviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Загрузка отзывов
  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/review/`);
      setReviews(res.data.reviews || []);
    } catch (error) {
      message.error("Ошибка при загрузке отзывов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line
  }, []);

  // Удаление отзыва
  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/review/${id}`);
      message.success("Отзыв удалён");
      fetchReviews();
    } catch (error) {
      message.error("Ошибка удаления");
    }
  };

  // Смена статуса: approve/reject через PUT
  const updateStatus = async (id: number, status: "approved" | "rejected") => {
    const url =
      status === "approved"
        ? `${API_URL}/review/${id}/approve`
        : `${API_URL}/review/${id}/reject`;
    try {
      await axios.put(url);
      // Тут именно alert, как просил:
      if (status === "approved") {
        alert("Успешно одобрено!");
      } else {
        alert("Успешно отклонено!");
      }
      // Просто обновляем данные, ничего не двигаем в таблице
      fetchReviews();
    } catch (error) {
      message.error("Ошибка смены статуса");
    }
  };

  const columns: ColumnsType<Review> = [
    { title: "ID", dataIndex: "id", width: 60, key: "id" },
    { title: "Имя", dataIndex: "name", width: 120, key: "name" },
    { title: "Фамилия", dataIndex: "surname", width: 120, key: "surname" },
    { title: "Отзыв", dataIndex: "text", width: 260, key: "text" },
    {
      title: "Дата",
      dataIndex: "created_at",
      render: (text: string) => dayjs(text).format("YYYY-MM-DD HH:mm"),
      width: 150,
      key: "created_at",
    },
    {
      title: "Статус",
      dataIndex: "status",
      width: 120,
      key: "status",
      render: (status: string) => (
        <Tag color={statusColors[status] || "default"}>
          {statusLabels[status] || status}
        </Tag>
      ),
      filters: [
        { text: "На модерации", value: "pending" },
        { text: "Одобрен", value: "approved" },
        { text: "Отклонён", value: "rejected" },
      ],
      onFilter: (value, record) => record.status === String(value),
    },
    {
      title: "Действия",
      key: "actions",
      width: 200,
      render: (_: any, record: Review) => (
        <Space>
          <Button
            icon={<CheckOutlined />}
            type={record.status === "approved" ? "primary" : "default"}
            disabled={record.status === "approved"}
            onClick={() => updateStatus(record.id, "approved")}
            size="small"
          >
            Соглосовать
          </Button>
          <Button
            icon={<StopOutlined />}
            danger
            type={record.status === "rejected" ? "primary" : "default"}
            disabled={record.status === "rejected"}
            onClick={() => updateStatus(record.id, "rejected")}
            size="small"
          >
            Отменить
          </Button>
          <Popconfirm
            title="Удалить отзыв?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Отзывы пользователей"
      extra={
        <Button icon={<ReloadOutlined />} onClick={fetchReviews}>
          Обновить
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Table
          dataSource={reviews}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </Card>
  );
};

export default AdminReviews;
